/**
 * useUserSearch - searchable customer-user picker hook.
 *
 * Backs the TicketOwnerPicker on the agent detail page. The backend
 * endpoint is ``GET /agent/users/search?q=&limit=`` (agent-only,
 * customer rows only). The hook handles two concerns the consumer
 * shouldn't have to think about:
 *
 *   1. Debounce. The composer fires a state update on every keystroke;
 *      we wait 250 ms of quiet before issuing the GET so a fast typist
 *      doesn't churn the server. The debounce lives here (not in the
 *      component) so any future picker reusing this hook gets the same
 *      protection.
 *
 *   2. Stable cache keys. The cache is keyed on the trimmed, lowercased
 *      query so two callers searching the same string share a cache
 *      entry. Empty-string queries are a valid call (returns the
 *      newest 20 customer rows) and the cache key reflects that.
 */
import { useEffect, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { AgentUserSearchResult } from "@/types/api";

const DEBOUNCE_MS = 250;

export const USER_SEARCH_QUERY_KEY_PREFIX = ["agent", "users", "search"] as const;

export function userSearchQueryKey(
  q: string,
  limit: number,
  ticketNumber: number | null,
): readonly unknown[] {
  return [
    ...USER_SEARCH_QUERY_KEY_PREFIX,
    q.trim().toLowerCase(),
    limit,
    ticketNumber,
  ] as const;
}

export interface UseUserSearchOptions {
  /** Soft cap on the response. Default 20 - the dropdown displays
   * about 8 visible rows and scrolls the rest. */
  limit?: number;
  /** Whether to actually fire requests. The consumer turns this on
   * when the dropdown opens and off when it closes so a closed
   * picker isn't polling the search endpoint on every keystroke
   * elsewhere on the page. */
  enabled?: boolean;
  /** Current ticket context. When provided AND the query is empty,
   * the backend pre-populates with customers at the same company as
   * the ticket's current requester (excluding the requester
   * themselves). Without this, an empty query returns an empty list
   * - browsing without context surfaces random unrelated customers,
   * which is the bug Greg reported on Lolita's ticket on 2026-05-17. */
  ticketNumber?: number | null;
}

export function useUserSearch(
  rawQ: string,
  options: UseUserSearchOptions = {},
): UseQueryResult<AgentUserSearchResult[]> & { debouncedQ: string } {
  const limit = options.limit ?? 20;
  const enabled = options.enabled !== false;
  const ticketNumber = options.ticketNumber ?? null;

  // Mirror the input value into a debounced state. The picker reads
  // ``debouncedQ`` to display the "Searching..." label between
  // typing and a request actually firing.
  const [debouncedQ, setDebouncedQ] = useState(rawQ);
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(rawQ), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [rawQ]);

  const result = useQuery<AgentUserSearchResult[]>({
    queryKey: userSearchQueryKey(debouncedQ, limit, ticketNumber),
    enabled,
    // Keep stale entries around for a minute so toggling the dropdown
    // closed and back open on the same query renders instantly.
    staleTime: 60_000,
    queryFn: async () => {
      // Build params explicitly so we omit ``ticket_number`` when it
      // isn't supplied. axios serializes ``null`` as the literal
      // string "null"; the backend would then try to parse it as int
      // and 422.
      const params: Record<string, string | number> = {
        q: debouncedQ,
        limit,
      };
      if (ticketNumber !== null) {
        params.ticket_number = ticketNumber;
      }
      const response = await api.get<AgentUserSearchResult[]>(
        "/agent/users/search",
        { params },
      );
      return response.data;
    },
  });

  return Object.assign(result, { debouncedQ });
}
